import {ChangeDetectionStrategy, Component, DestroyRef, Type, ViewChild, computed, effect, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {PageEvent} from '@angular/material/paginator';
import {ManagementStore, TableRow} from './management.store';
import {DrawerService} from '../../services/drawer.service';
import {RoutePaths} from '../../shared/types/common';
import {Account, AiTopicRow, Brand, SortState, User} from './types';
import {SearchBarComponent} from '../../shared/components/search-bar/search-bar.component';
import {ButtonComponent} from '../../shared/components/button/button.component';
import {PaginatorComponent} from '../../shared/components/paginator/paginator.component';
import {LoaderComponent} from '../../shared/components/loader/loader.component';
import {AccountsTableComponent} from './accounts-table/accounts-table.component';
import {UsersTableComponent} from './users-table/users-table.component';
import {BrandsTableComponent} from './brands-table/brands-table.component';
import {AccountDrawerComponent} from './account-drawer/account-drawer.component';
import {UserDrawerComponent} from './user-drawer/user-drawer.component';
import {BrandDrawerComponent} from './brand-drawer/brand-drawer.component';
import {AiTopicsTableComponent} from './ai-topics-table/ai-topics-table.component';
import {TopicDrawerComponent} from './topic-drawer/topic-drawer.component';
import {AuthorizationService} from '../../services/authorization.service';
import {Permissions} from '../../shared/permissions';

const ADD_LABEL: Record<string, string> = {accounts: '+ Add Account', users: '+ Add User', brands: '+ Add Brand', 'ai-presence': '+ Add Topic'};
// The empty-state / error copy names what the rows are — for ai-presence the rows are topics.
const ROWS_LABEL: Record<string, string> = {'ai-presence': 'topics'};
// Wide but responsive: never wider than the viewport allows on small screens
const DRAWER_WIDTH = 'min(1200px, 95vw)';

@Component({
  selector: 'app-management-screen',
  imports: [
    SearchBarComponent,
    ButtonComponent,
    PaginatorComponent,
    LoaderComponent,
    AccountsTableComponent,
    UsersTableComponent,
    BrandsTableComponent,
    AiTopicsTableComponent,
  ],
  templateUrl: './management-screen.component.html',
  styleUrl: './management-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagementScreenComponent {
  private readonly authorization = inject(AuthorizationService);
  // The AI Presence tab is permission-gated separately — the screen-level route gate is an AND
  // over account-management permissions and must not grow for it.
  // Brands first — it is the default tab (the bare /manage route redirects to tabs[0])
  public readonly tabs = ['brands', ...(this.authorization.hasPermissions([Permissions.READ_AI_TOPIC]) ? ['ai-presence'] : []), 'accounts', 'users'];

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly drawer = inject(DrawerService);
  private readonly store = inject(ManagementStore);

  protected readonly searchControl = new FormControl<string>('', {nonNullable: true});
  @ViewChild(SearchBarComponent) private searchBar?: SearchBarComponent;

  // --- view state (read straight from the store) ---
  protected readonly activeTab = this.store.activeTab;
  protected readonly total = this.store.total;
  protected readonly loading = this.store.loading;
  protected readonly error = this.store.error;
  protected readonly promptsTruncated = this.store.promptsTruncated;
  protected readonly pageSize = this.store.pageSize;
  protected readonly paginatorPage = computed(() => this.store.page() + 1);
  protected readonly addButtonText = computed(() => ADD_LABEL[this.activeTab()] ?? '');
  // On ai-presence only write:ai-topic holders can create topics; the other tabs are gated at the route.
  protected readonly canAdd = computed(() => this.activeTab() !== 'ai-presence' || this.authorization.hasPermissions([Permissions.WRITE_AI_TOPIC]));
  protected readonly rowsLabel = computed(() => ROWS_LABEL[this.activeTab()] ?? this.activeTab());
  protected readonly empty = computed(() => !this.loading() && !this.error() && this.store.rows().length === 0);

  protected readonly accountRows = computed(() => this.store.rows() as Account[]);
  protected readonly userRows = computed(() => this.store.rows() as User[]);
  protected readonly brandRows = computed(() => this.store.rows() as Brand[]);
  protected readonly aiTopicRows = computed(() => this.store.rows() as AiTopicRow[]);

  constructor() {
    // Keep the search box mirrored to the store's term (the single source of truth). This covers the
    // term changes the input doesn't make itself: the add-brand "filter to the new brand", tab resets,
    // and same-tab revisits (the store is a singleton; this component is recreated each visit).
    effect(() => this.searchControl.setValue(this.store.term(), {emitEvent: false}));

    // The active tab is driven reactively by the route param — no snapshot reads, so it never lags
    // navigation. setTab is a no-op when already on that tab, so revisiting keeps cached rows.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab') ?? '';
      if (!this.tabs.includes(tab)) {
        this.router.navigate(['/', RoutePaths.MANAGE, this.tabs[0]], {replaceUrl: true});
        return;
      }
      this.store.setTab(tab);
      // The search bar survives tab switches — put the cursor back so typing works immediately
      this.searchBar?.focus();
    });
  }

  switchTab(tab: string): void {
    if (tab === this.activeTab()) {
      return;
    }
    this.router.navigate(['/', RoutePaths.MANAGE, tab]);
  }

  onSearch(term: string): void {
    this.store.setSearch(term ?? '');
  }

  onPageChange(event: PageEvent): void {
    this.store.setPage(event.pageIndex);
  }

  onSortChanged(sort: SortState): void {
    this.store.setSort(sort);
  }

  retry(): void {
    this.store.reload();
  }

  onRowClicked(row: TableRow): void {
    switch (this.activeTab()) {
      case 'accounts':
        this.openDrawer(AccountDrawerComponent, row as Account);
        break;
      case 'users':
        this.openDrawer(UserDrawerComponent, row as User);
        break;
      case 'brands':
        this.openDrawer(BrandDrawerComponent, row as Brand);
        break;
      case 'ai-presence':
        this.openDrawer(TopicDrawerComponent, row as AiTopicRow);
        break;
    }
  }

  add(): void {
    switch (this.activeTab()) {
      case 'accounts':
        this.openDrawer(AccountDrawerComponent);
        break;
      case 'users':
        this.openDrawer(UserDrawerComponent);
        break;
      case 'brands':
        this.openDrawer(BrandDrawerComponent);
        break;
      case 'ai-presence':
        this.openDrawer(TopicDrawerComponent);
        break;
    }
  }

  // Opens a form drawer. The drawer owns its own submit/save (via the store), so there's no
  // afterClosed result to process here — the store already refreshed the list and the dropdown caches.
  private openDrawer<T>(component: Type<T>, entity?: TableRow): void {
    const ref = this.drawer.open(component, {
      width: DRAWER_WIDTH,
      data: {entity, close: () => ref.close()},
    });
  }
}
